import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ParkVisit {
  parkId: string;
  childId: string;
  visitDate: string;
  scavengerItemsFound: string[]; // item names checked off
  itemPhotos?: Record<string, string>; // item name -> idb:: photo URL
  photoCount: number;
  notes: string;
  completed: boolean; // true if all scavenger items found
}

interface ParkPassportState {
  visits: ParkVisit[];

  addVisit: (visit: Omit<ParkVisit, 'completed'>) => void;
  updateVisit: (parkId: string, childId: string, updates: Partial<ParkVisit>) => void;
  getVisitsForChild: (childId: string) => ParkVisit[];
  getVisit: (parkId: string, childId: string) => ParkVisit | undefined;
  getVisitedParkIds: (childId: string) => string[];
  getTotalParksVisited: (childId: string) => number;
}

export const useParkPassportStore = create<ParkPassportState>()(
  persist(
    (set, get) => ({
      visits: [],

      addVisit: (visit) => {
        const existing = get().visits.find(
          v => v.parkId === visit.parkId && v.childId === visit.childId
        );
        if (existing) return; // Already visited

        set(state => ({
          visits: [...state.visits, { ...visit, completed: false }],
        }));
      },

      updateVisit: (parkId, childId, updates) => {
        set(state => ({
          visits: state.visits.map(v =>
            v.parkId === parkId && v.childId === childId
              ? { ...v, ...updates }
              : v
          ),
        }));
      },

      getVisitsForChild: (childId) => {
        return get().visits.filter(v => v.childId === childId);
      },

      getVisit: (parkId, childId) => {
        return get().visits.find(
          v => v.parkId === parkId && v.childId === childId
        );
      },

      getVisitedParkIds: (childId) => {
        return get().visits
          .filter(v => v.childId === childId)
          .map(v => v.parkId);
      },

      getTotalParksVisited: (childId) => {
        return get().visits.filter(v => v.childId === childId).length;
      },
    }),
    { name: 'homeschool-park-passport', version: 0, migrate: (state) => state }
  )
);
